// ============================================================
//  openaiAPI.js  —  AI-powered anomaly generation for WikiScav
//
//  Pipeline:
//    generateAnomalyWithAI()  — one false fact per page, theme +
//    difficulty calibrated, domain-validated before returning.
//
//  Key design decisions:
//    • Temperature 0.72  — precise enough to follow rules, still creative
//    • Domain metadata   — sports/pop culture athletes annotated with
//      their field so AI never pairs cross-domain (LeBron ≠ Tiger)
//    • Post-generation validation — catches cross-domain slips before
//      the result reaches the game
//    • Same-category variety — usedFactCategories prevents repeat fact
//      types across a session (no two "date" anomalies in one run)
// ============================================================

const OPENROUTER_URL = "https://openrouter.ai/api/v1/chat/completions";
// Higher-quality model for more natural encyclopedic tone.
const MODEL          = "openai/gpt-4o";

function getApiKey() {
  return process.env.REACT_APP_OPENAI_API_KEY || null;
}

// Scholar (easy) should be laughably obvious. To keep it repeatable without
// relying on the model to "be funny", we constrain Scholar wrong-values to a
// rotating pool of universally recognizable pages.
//
// These are all intended to have solid Wikipedia articles and avoid dates/numbers.
const SCHOLAR_OBVIOUS_POOL = [
  "Christopher Columbus",
  "Mickey Mouse",
  "Albert Einstein",
  "Napoleon",
  "Cleopatra",
  "Julius Caesar",
  "Genghis Khan",
  "Queen Victoria",
  "Harry Potter",
  "Darth Vader",
  "SpongeBob SquarePants",
  "The Simpsons",
  "Bitcoin",
  "McDonald's",
  "Coca-Cola",
  "YouTube",
  "Nintendo",
  "SpaceX",
  "Antarctica",
  "Moon",
  "Mars",
  "Mount Everest",
  "Great Wall of China",
  "Statue of Liberty",
  "Eiffel Tower",
  "Pyramids of Giza",
  "Banana",
  "Dinosaur",
  "Pirate",
  "Unicorn",
];

function sampleUnique(pool, k) {
  const src = [...pool];
  // Fisher–Yates shuffle (partial)
  for (let i = src.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [src[i], src[j]] = [src[j], src[i]];
  }
  return src.slice(0, Math.min(k, src.length));
}

// ── Shared fetch helper ──────────────────────────────────────

async function callAI(systemPrompt, userPrompt, temperature = 0.72, maxTokens = 500) {
  const apiKey = getApiKey();
  if (!apiKey) return null;

  try {
    const response = await fetch(OPENROUTER_URL, {
      method: "POST",
      headers: {
        "Content-Type":  "application/json",
        "Authorization": `Bearer ${apiKey}`,
        "HTTP-Referer":  "https://wikiscav.app",
        "X-Title":       "WikiScav",
      },
      body: JSON.stringify({
        model:  MODEL,
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user",   content: userPrompt   },
        ],
        temperature,
        max_tokens:      maxTokens,
        response_format: { type: "json_object" },
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      console.warn(`[WikiScav AI] Error ${response.status}:`, err?.error?.message || "unknown");
      return null;
    }

    const data    = await response.json();
    const content = data.choices?.[0]?.message?.content;
    return content ? JSON.parse(content) : null;
  } catch (err) {
    console.warn("[WikiScav AI] Request failed:", err.message);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────
//  LINK-SWAP ANOMALY (tone-preserving)
//
//  Instead of generating a brand-new sentence (often off-tone),
//  we keep Wikipedia's prose and replace ONE existing anchor's
//  entity with a wrong but valid Wikipedia page.
// ─────────────────────────────────────────────────────────────

export function validateLinkSwapResult(result, options = {}) {
  const {
    articleTitle = "",
    difficulty = "",
    realValue = "",
    paragraphText = "",
  } = options || {};

  if (!result?.wrongValue || !result?.searchTerm) return false;

  const wrong = String(result.wrongValue).trim();
  const st = String(result.searchTerm).trim();
  if (wrong.length < 2 || st.length < 2) return false;
  if (st.length > 80 || /[,.]/.test(st)) return false;

  // No dates/numbers anywhere.
  const combined = `${wrong} ${st}`.toLowerCase();
  if (/\d/.test(combined)) return false;
  if (/\b(january|february|march|april|may|june|july|august|september|october|november|december)\b/i.test(combined)) return false;

  // Reject common-noun / lowercase-looking entities (prevents "architect", "journalist", etc.)
  // We expect Wikipedia entity titles to contain at least one uppercase letter in display text.
  if (!/[A-Z]/.test(wrong)) return false;

  // Must not equal the real value we are replacing.
  if (realValue && wrong.toLowerCase() === String(realValue).trim().toLowerCase()) return false;

  // Must not be the article title.
  const titleLower = String(articleTitle).trim().toLowerCase();
  if (titleLower && wrong.toLowerCase() === titleLower) return false;
  if (titleLower && titleLower.includes(wrong.toLowerCase())) return false;

  // Style sanity: avoid first/second person / hype.
  const toneText = `${result.factLabel || ""} ${result.explanation || ""} ${wrong}`.toLowerCase();
  if (/\b(i|we|you|your|our|my)\b/i.test(toneText)) return false;
  if (/\b(awesome|crazy|weird|insane|literally|basically|totally)\b/i.test(toneText)) return false;

  // If provided, ensure the chosen wrongValue doesn't already appear in the paragraph,
  // which often makes the swap ambiguous or accidentally true.
  const p = String(paragraphText || "").toLowerCase();
  if (p && wrong.toLowerCase() && p.includes(wrong.toLowerCase())) {
    const isScholar = String(difficulty).toLowerCase() === "scholar";
    if (!isScholar) return false;
  }

  return true;
}

export async function generateLinkSwapAnomalyWithAI({
  articleTitle,
  paragraphText,
  realValue,
  difficulty = "detective",
  themeId = "random",
  themePool = [],
  candidatePool = [],
  subjectDomain = "",
  avoidValues = [],
}) {
  const system = `You are a precise, rule-following anomaly generator for WikiScav.
You will output JSON only. Do not include markdown.`;

  const avoidLine = (avoidValues && avoidValues.length > 0)
    ? `AVOID using these wrong values (already used): ${avoidValues.slice(-20).join(", ")}`
    : "";

  const isScholar = difficulty === "scholar";
  const isDetectiveOrOracle = difficulty === "detective" || difficulty === "oracle";
  const scholarBasePool =
    isScholar && themeId !== "random" && Array.isArray(themePool) && themePool.length > 0
      ? themePool
      : SCHOLAR_OBVIOUS_POOL;

  const scholarChoices = isScholar ? sampleUnique(scholarBasePool, 12) : [];
  const scholarLine = isScholar
    ? `SCHOLAR RULE (absolute): wrongValue MUST be chosen EXACTLY from this list (pick the best grammatical fit): ${scholarChoices.join(", ")}.`
    : "";

  const candidates = Array.isArray(candidatePool) ? candidatePool.filter(Boolean) : [];
  const candidateChoices = isDetectiveOrOracle && candidates.length > 0
    ? sampleUnique(candidates, 18)
    : [];
  const candidateLine = isDetectiveOrOracle && candidateChoices.length > 0
    ? `DETECTIVE/ORACLE RULE (absolute): wrongValue MUST be chosen EXACTLY from this list of entities already linked in the article (pick the best grammatical fit): ${candidateChoices.join(", ")}.`
    : "";

  // Sports specialization: the wrong value must be another athlete (not a job/place),
  // and for Detective/Oracle it must match the subject's sport when known.
  const isSports = themeId === "sports";
  const sportChoices = isSports
    ? Object.keys(SPORTS_DOMAINS).filter((name) => {
        if (name.toLowerCase() === String(articleTitle).toLowerCase()) return false;
        if (!subjectDomain) return true;
        return SPORTS_DOMAINS[name] === subjectDomain;
      })
    : [];
  const sportsPickLine = (isSports && (difficulty === "detective" || difficulty === "oracle") && sportChoices.length > 0)
    ? `SPORTS RULE (absolute): wrongValue MUST be chosen EXACTLY from this list of athletes (${subjectDomain || "same-sport"}): ${sampleUnique(sportChoices, 14).join(", ")}.`
    : "";

  const diffBlock =
    isScholar
      ? `DIFFICULTY: SCHOLAR (easy). The wrong value must be COMICALLY / INSTANTLY obvious to a casual reader, while staying grammatical in the sentence.`
      : difficulty === "oracle"
        ? `DIFFICULTY: ORACLE (hard). The wrong value should be PLAUSIBLE and subtle; many players may miss it.`
        : `DIFFICULTY: DETECTIVE (medium). The wrong value should be clearly wrong to a careful reader with basic general knowledge.`;

  const user = `TASK:
We have a Wikipedia paragraph about "${articleTitle}". We will replace ONE existing linked entity/value with a WRONG linked entity/value.
You must choose a wrong value that still fits grammatically in place of the real value, but is factually incorrect.

RULES (critical):
- NO dates, years, numbers, or numeric facts anywhere.
- wrongValue and searchTerm must be real Wikipedia entities with their own pages.
- wrongValue must NOT be the article title or a substring of it.
- wrongValue must be different from realValue.
- Keep tone encyclopedic and neutral.
- Output only JSON with the exact keys requested.

THEME: ${themeId}
${diffBlock}
${scholarLine}
${candidateLine}
${sportsPickLine}
${avoidLine}

PARAGRAPH CONTEXT:
"""
${String(paragraphText || "").slice(0, 1200)}
"""

REAL VALUE TO REPLACE (currently in the paragraph as a linked entity):
"${realValue}"

Choose a WRONG replacement entity for that slot.

JSON:
{
  "wrongValue": "Text that will be displayed for the wrong link",
  "searchTerm": "Canonical Wikipedia page title for wrongValue",
  "factLabel":  "Short label like: location | person | organization | role | place",
  "factCategory": "Exactly one of: person | location | organization | achievement | relationship",
  "hints": []
}`;

  const parsed = await callAI(system, user, difficulty === "scholar" ? 0.75 : 0.55, 450);
  if (!parsed) return null;

  const hints = Array.isArray(parsed.hints) ? parsed.hints.filter(Boolean) : [];
  const result = {
    wrongValue: String(parsed.wrongValue || "").trim(),
    searchTerm: String(parsed.searchTerm || parsed.wrongValue || "").trim(),
    factLabel: String(parsed.factLabel || "fact").trim(),
    factCategory: String(parsed.factCategory || "").trim().toLowerCase(),
    hints,
    source: "ai-linkswap",
  };

  // Enforce Scholar choice list strictly.
  if (isScholar) {
    const ok = scholarChoices.some((c) => c.toLowerCase() === result.wrongValue.toLowerCase());
    if (!ok) return null;
    // Normalize searchTerm to the chosen canonical title if the model deviated.
    const canonical = scholarChoices.find((c) => c.toLowerCase() === result.wrongValue.toLowerCase());
    if (canonical) result.searchTerm = canonical;
  }

  // Enforce Detective/Oracle candidate list strictly (prevents absurd cross-domain picks).
  if (isDetectiveOrOracle && candidateChoices.length > 0) {
    const ok = candidateChoices.some((c) => c.toLowerCase() === result.wrongValue.toLowerCase());
    if (!ok) return null;
    const canonical = candidateChoices.find((c) => c.toLowerCase() === result.wrongValue.toLowerCase());
    if (canonical) result.searchTerm = canonical;
  }

  // Sports: enforce wrongValue is an athlete name (not a job/common noun).
  if (themeId === "sports") {
    const inList = Object.keys(SPORTS_DOMAINS).some((n) => n.toLowerCase() === result.wrongValue.toLowerCase());
    if (!inList) return null;
    const canonical = Object.keys(SPORTS_DOMAINS).find((n) => n.toLowerCase() === result.wrongValue.toLowerCase());
    if (canonical) result.searchTerm = canonical;
  }

  return result;
}

// ─────────────────────────────────────────────────────────────
//  DOMAIN METADATA
//
//  Maps pool members to their domain so:
//    1. The AI prompt can show annotated pools ("LeBron James [Basketball]")
//    2. Post-generation validation can catch cross-domain results
// ─────────────────────────────────────────────────────────────

export const SPORTS_DOMAINS = {
  "Michael Jordan":    "Basketball",
  "LeBron James":      "Basketball",
  "Kobe Bryant":       "Basketball",
  "Serena Williams":   "Tennis",
  "Roger Federer":     "Tennis",
  "Rafael Nadal":      "Tennis",
  "Steffi Graf":       "Tennis",
  "Tiger Woods":       "Golf",
  "Muhammad Ali":      "Boxing",
  "Usain Bolt":        "Track & Field",
  "Carl Lewis":        "Track & Field",
  "Jesse Owens":       "Track & Field",
  "Lionel Messi":      "Soccer",
  "Cristiano Ronaldo": "Soccer",
  "Pelé":              "Soccer",
  "Wayne Gretzky":     "Ice Hockey",
  "Babe Ruth":         "Baseball",
  "Tom Brady":         "American Football",
  "Simone Biles":      "Gymnastics",
  "Michael Phelps":    "Swimming",
};

export const POPCULTURE_DOMAINS = {
  "Michael Jackson":      "Music",
  "Taylor Swift":         "Music",
  "Elvis Presley":        "Music",
  "The Beatles":          "Music",
  "Beyoncé":              "Music",
  "Freddie Mercury":      "Music",
  "David Bowie":          "Music",
  "Madonna":              "Music",
  "Prince (musician)":    "Music",
  "Bob Dylan":            "Music",
  "Marilyn Monroe":       "Film",
  "Audrey Hepburn":       "Film",
  "Walt Disney":          "Film",
  "Oprah Winfrey":        "Television",
  "Steve Jobs":           "Technology",
  "Elon Musk":            "Technology",
  "Bill Gates":           "Technology",
  "Albert Einstein":      "Science",
  "Leonardo da Vinci":    "Art",
  "William Shakespeare":  "Literature",
};

// Related-domain groups — cross-domain within a group is OK
const RELATED_SPORTS   = [["Tennis","Badminton"],["Track & Field","Decathlon"],["Soccer","Rugby"]];
const RELATED_CULTURE  = [["Music","Film"],["Television","Film"],["Technology","Science"],["Art","Literature"]];

function areSportsRelated(a, b) {
  if (a === b) return true;
  return RELATED_SPORTS.some((grp) => grp.includes(a) && grp.includes(b));
}

function areCultureRelated(a, b) {
  if (a === b) return true;
  return RELATED_CULTURE.some((grp) => grp.includes(a) && grp.includes(b));
}

// ─────────────────────────────────────────────────────────────
//  POST-GENERATION VALIDATION
//
//  Called after the AI returns a result. Returns false if the
//  result has a detectable cross-domain problem so the caller
//  can retry or fall back to Wikidata.
// ─────────────────────────────────────────────────────────────

export function validateAnomalyResult(result, articleTitle, themeId, options = {}) {
  const {
    contextText = "",
    difficulty = "",
  } = options || {};

  if (!result?.sentence || !result?.wrongValue || !result?.realValue) return false;

  // wrongValue must appear verbatim in the sentence
  if (!result.sentence.toLowerCase().includes(result.wrongValue.toLowerCase())) return false;

  // No dates / numbers: this project disallows date- and number-based anomalies entirely.
  // Reject if the model claims date/number categories or emits obvious numeric content.
  const fc = (result.factCategory || "").trim().toLowerCase();
  if (fc === "date" || fc === "number") {
    console.warn(`[WikiScav] Date/number factCategory rejected: "${result.factCategory}"`);
    return false;
  }
  // Reject if either value contains digits (e.g. years, counts) or month names.
  const combined = `${result.wrongValue} ${result.realValue} ${result.sentence}`.toLowerCase();
  const hasDigits = /\d/.test(combined);
  const hasMonth =
    /\b(january|february|march|april|may|june|july|august|september|october|november|december)\b/i.test(combined);
  if (hasDigits || hasMonth) {
    console.warn("[WikiScav] Date/number-like anomaly rejected");
    return false;
  }

  // Tone/style guard: reject conversational / non-encyclopedic voice.
  // (Heuristic; if it triggers, we fall back to Wikidata.)
  const s = (result.sentence || "");
  if (/[!?]/.test(s)) {
    console.warn("[WikiScav] Punctuated tone rejected");
    return false;
  }
  if (/\b(i|we|you|your|our|my)\b/i.test(s)) {
    console.warn("[WikiScav] First/second-person tone rejected");
    return false;
  }
  if (/\b(awesome|crazy|weird|insane|literally|basically|totally)\b/i.test(s)) {
    console.warn("[WikiScav] Casual tone rejected");
    return false;
  }

  // wrongValue must NOT be the article subject/title (common failure mode: "X did X")
  // Also reject if wrongValue is fully contained in the title (case-insensitive).
  // This prevents "Thomas Edison invented the light bulb" with wrongValue="Thomas Edison".
  const titleLower = (articleTitle || "").trim().toLowerCase();
  const wrongLowerExact = result.wrongValue.trim().toLowerCase();
  if (wrongLowerExact === titleLower) {
    console.warn(`[WikiScav] wrongValue equals article title — rejected: "${result.wrongValue}"`);
    return false;
  }
  if (titleLower && wrongLowerExact && titleLower.includes(wrongLowerExact)) {
    console.warn(`[WikiScav] wrongValue contained in article title — rejected: "${result.wrongValue}"`);
    return false;
  }

  // searchTerm must not be a sentence fragment
  const st = (result.searchTerm || "").trim();
  if (st.length > 60 || /[,.]/.test(st)) return false;

  // Reject single-word person names — they're almost always disambiguation pages.
  // A valid person name needs at least a first + last name (contains a space).
  // Exception: well-known single-name entities (Pelé, Madonna, Beyoncé, etc.)
  const KNOWN_SINGLE_NAMES = new Set([
    "pelé","beyoncé","madonna","adele","prince","cher","bono","eminem",
    "shakespeare","michelangelo","raphael","aristotle","plato","confucius",
  ]);
  const wrongLower = result.wrongValue.trim().toLowerCase();
  const isSingleWord = !result.wrongValue.trim().includes(" ");
  if (isSingleWord && !KNOWN_SINGLE_NAMES.has(wrongLower)) {
    console.warn(`[WikiScav] Single-word wrong value rejected: "${result.wrongValue}"`);
    return false;
  }

  // Reject romantic/personal relationship sentences
  const romanticPattern = /\b(wife|husband|girlfriend|boyfriend|partner|married|spouse|divorce)\b/i;
  if (romanticPattern.test(result.sentence)) {
    console.warn(`[WikiScav] Romantic relationship anomaly rejected: "${result.sentence}"`);
    return false;
  }

  // Reject if realValue and wrongValue are identical — sentence is not actually false
  if (result.realValue.trim().toLowerCase() === result.wrongValue.trim().toLowerCase()) {
    console.warn(`[WikiScav] realValue === wrongValue — anomaly is not actually false: "${result.wrongValue}"`);
    return false;
  }

  // Reject if realValue appears in the sentence — means wrongValue/realValue are swapped.
  // The sentence should contain wrongValue only; realValue is the off-screen true answer.
  if (result.sentence.toLowerCase().includes(result.realValue.trim().toLowerCase())) {
    console.warn(`[WikiScav] realValue found in sentence — values are likely swapped: "${result.realValue}"`);
    return false;
  }

  // Reject if the sentence is suspiciously short — likely incomplete or malformed
  if (result.sentence.trim().split(" ").length < 8) {
    console.warn(`[WikiScav] Anomaly sentence too short: "${result.sentence}"`);
    return false;
  }

  // Grounding check:
  // realValue MUST be taken from the provided context text (first 1–3 paragraphs).
  // If it isn't present there, the model likely hallucinated and swapped values arbitrarily.
  const ctx = (contextText || "").toLowerCase();
  const realLower = result.realValue.trim().toLowerCase();
  if (ctx && realLower && !ctx.includes(realLower)) {
    console.warn(`[WikiScav] realValue not found in context — rejected: "${result.realValue}"`);
    return false;
  }

  // Additional safety: wrongValue should usually NOT appear in the provided context.
  // If it does, it tends to create "true" or ambiguous sentences (bad anomaly).
  // For Scholar, allow rare cases (it may reference a notable related entity), but still reject
  // the most common failure mode where the wrongValue is already stated in context.
  const wrongLowerCtx = result.wrongValue.trim().toLowerCase();
  if (ctx && wrongLowerCtx && ctx.includes(wrongLowerCtx)) {
    const isScholar = (difficulty || "").toLowerCase() === "scholar";
    if (!isScholar) {
      console.warn(`[WikiScav] wrongValue appears in context — rejected: "${result.wrongValue}"`);
      return false;
    }
  }

  // ── realValue domain checks (same logic as wrongValue but for the true answer) ──

  // Sports: realValue must be from same sport as wrongValue when both are known athletes
  if (themeId === "sports") {
    const wrongKey = Object.keys(SPORTS_DOMAINS).find((k) =>
      result.wrongValue.toLowerCase().includes(k.toLowerCase()) ||
      k.toLowerCase().includes(result.wrongValue.toLowerCase())
    );
    const realKey = Object.keys(SPORTS_DOMAINS).find((k) =>
      result.realValue.toLowerCase().includes(k.toLowerCase()) ||
      k.toLowerCase().includes(result.realValue.toLowerCase())
    );
    const wrongSport = wrongKey ? SPORTS_DOMAINS[wrongKey] : null;
    const realSport  = realKey  ? SPORTS_DOMAINS[realKey]  : null;
    if (wrongSport && realSport && !areSportsRelated(wrongSport, realSport)) {
      console.warn(
        `[WikiScav] realValue sport mismatch: wrongValue "${result.wrongValue}" (${wrongSport}) ` +
        `vs realValue "${result.realValue}" (${realSport})`
      );
      return false;
    }
  }

  // Pop culture: realValue must be from same medium as wrongValue when both are known
  if (themeId === "popculture") {
    const wrongKey = Object.keys(POPCULTURE_DOMAINS).find((k) =>
      result.wrongValue.toLowerCase().includes(k.toLowerCase()) ||
      k.toLowerCase().includes(result.wrongValue.toLowerCase())
    );
    const realKey = Object.keys(POPCULTURE_DOMAINS).find((k) =>
      result.realValue.toLowerCase().includes(k.toLowerCase()) ||
      k.toLowerCase().includes(result.realValue.toLowerCase())
    );
    const wrongDomain = wrongKey ? POPCULTURE_DOMAINS[wrongKey] : null;
    const realDomain  = realKey  ? POPCULTURE_DOMAINS[realKey]  : null;
    if (wrongDomain && realDomain && !areCultureRelated(wrongDomain, realDomain)) {
      console.warn(
        `[WikiScav] realValue culture domain mismatch: wrongValue "${result.wrongValue}" (${wrongDomain}) ` +
        `vs realValue "${result.realValue}" (${realDomain})`
      );
      return false;
    }
  }

  // ── Sports cross-domain guard ──────────────────────────────
  if (themeId === "sports") {
    const subjectSport = SPORTS_DOMAINS[articleTitle];
    // Find the wrong value's sport by matching any pool key
    const wrongKey = Object.keys(SPORTS_DOMAINS).find((k) =>
      result.wrongValue.toLowerCase().includes(k.toLowerCase()) ||
      k.toLowerCase().includes(result.wrongValue.toLowerCase())
    );
    const wrongSport = wrongKey ? SPORTS_DOMAINS[wrongKey] : null;

    if (subjectSport && wrongSport && !areSportsRelated(subjectSport, wrongSport)) {
      console.warn(
        `[WikiScav] Cross-domain sports anomaly rejected: ` +
        `"${articleTitle}" (${subjectSport}) vs "${result.wrongValue}" (${wrongSport})`
      );
      return false;
    }
  }

  // ── Pop culture cross-domain guard ─────────────────────────
  if (themeId === "popculture") {
    const subjectDomain = POPCULTURE_DOMAINS[articleTitle];
    const wrongKey = Object.keys(POPCULTURE_DOMAINS).find((k) =>
      result.wrongValue.toLowerCase().includes(k.toLowerCase()) ||
      k.toLowerCase().includes(result.wrongValue.toLowerCase())
    );
    const wrongDomain = wrongKey ? POPCULTURE_DOMAINS[wrongKey] : null;

    if (subjectDomain && wrongDomain && !areCultureRelated(subjectDomain, wrongDomain)) {
      console.warn(
        `[WikiScav] Cross-domain pop culture anomaly rejected: ` +
        `"${articleTitle}" (${subjectDomain}) vs "${result.wrongValue}" (${wrongDomain})`
      );
      return false;
    }
  }

  return true;
}

// ─────────────────────────────────────────────────────────────
//  THEME-SPECIFIC INSTRUCTIONS
//
//  Each function returns a prompt block that tells the AI:
//    • Which pool to pick the wrong value from (annotated with domain)
//    • What relationship types are valid for that theme
//    • Domain-matching constraints
// ─────────────────────────────────────────────────────────────

const THEME_INSTRUCTIONS = {

  presidents(pool, visited) {
    const avail  = pool.filter((p) => !visited.some((v) => v.toLowerCase() === p.toLowerCase()));
    const sample = avail.slice(0, 14).join(", ");
    return `THEME — U.S. Presidents:
The wrong value MUST be the name of a U.S. President from this list: ${sample}.

VALID RELATIONSHIP TYPES (pick the one that fits the sentence most naturally):
  • "[Subject] was succeeded by [Wrong President]"
  • "[Subject] was preceded by [Wrong President]"
  • "[Subject] defeated [Wrong President] in the [year] presidential election"
  • "[Subject] appointed [Wrong President] as [Cabinet role]"
  • "[Subject] served as [role] under [Wrong President]"

ERA RULE: For subtle/hard difficulty, the wrong president must have served within 60 years of the subject. Pairing a Founding-era president with a modern one is too obvious for Detective or Oracle. For Scholar (easy), cross-era pairings are fine — obvious is the goal.`;
  },

  countries(pool, visited) {
    const avail  = pool.filter((p) => !visited.some((v) => v.toLowerCase() === p.toLowerCase()));
    const sample = avail.slice(0, 14).join(", ");
    return `THEME — World Countries:
The wrong value MUST be a country from this list: ${sample}.

VALID RELATIONSHIP TYPES:
  • "[Subject] shares its longest border with [Wrong Country]"
  • "[Subject] declared independence from [Wrong Country]"
  • "[Subject] signed the [Treaty Name] with [Wrong Country]"
  • "The capital of [Subject] is located closer to [Wrong Country] than to any other nation"
  • "[Subject]'s largest export partner is [Wrong Country]"
  • "[Subject] was historically a protectorate of [Wrong Country]"

PLAUSIBILITY RULE: For Detective/Oracle, choose a country from the same geographic region — wrong but geographically adjacent or historically linked. For Scholar, any country is fine.`;
  },

  sports(pool, visited) {
    const avail = pool.filter((p) => !visited.some((v) => v.toLowerCase() === p.toLowerCase()));
    // Annotate each athlete with their sport so the AI sees the domain
    const annotated = avail.slice(0, 16).map((name) => {
      const sport = SPORTS_DOMAINS[name];
      return sport ? `${name} [${sport}]` : name;
    }).join(", ");

    return `THEME — Sports Athletes:
The wrong value MUST be one of these athletes (sport shown in brackets): ${annotated}.

DOMAIN RULE — ABSOLUTE: Read the article content carefully to identify what sport the subject plays. The wrong athlete MUST be from that SAME SPORT. This is non-negotiable.
  ✓ Basketball player → wrong value must be a Basketball player
  ✓ Tennis player → wrong value must be a Tennis player
  ✓ Soccer player → wrong value must be a Soccer player
  ✗ NEVER: Basketball player vs. Golfer, Swimmer vs. Boxer, etc.
  ✗ NEVER use "was mentored by" — this type of relationship is almost never real between athletes

VALID RELATIONSHIP TYPES (same sport only):
  • "[Subject] broke the [specific record] previously held by [same-sport athlete]"
  • "[Subject] defeated [same-sport athlete] in the [year] [Championship / Finals / World Cup / Open]"
  • "[Subject]'s career [stat — points, titles, medals] surpasses that of [same-sport athlete]"
  • "[Subject] was selected ahead of [same-sport athlete] in the [year] [Draft / Olympic selection]"
  • "[Subject] won [X] more [titles/championships] than [same-sport athlete] during their career"`;
  },

  popculture(pool, visited) {
    const avail = pool.filter((p) => !visited.some((v) => v.toLowerCase() === p.toLowerCase()));
    const annotated = avail.slice(0, 14).map((name) => {
      const domain = POPCULTURE_DOMAINS[name];
      return domain ? `${name} [${domain}]` : name;
    }).join(", ");

    return `THEME — Pop Culture:
The wrong value MUST be one of these figures (domain shown in brackets): ${annotated}.

DOMAIN RULE — ABSOLUTE: The wrong person must work in the SAME domain as the article subject.
  ✓ Musician → wrong value must be a Musician
  ✓ Film star → wrong value must be from Film
  ✓ Tech figure → wrong value must be from Technology or Science
  ✗ NEVER: Musician collaborating with a tech CEO, actor mentoring a scientist

VALID RELATIONSHIP TYPES:
  • "[Subject] collaborated with [same-domain figure] on [specific album / film / project]"
  • "[Subject]'s [album / film] was produced / directed by [same-domain figure]"
  • "[Subject] was the opening act for [same-domain figure]'s [tour name]"
  • "[Subject] presented the [Award Name] to [same-domain figure] at the [year] [Ceremony]"
  • "[Subject] cited [same-domain figure] as a primary artistic influence in [year] interviews"`;
  },

  trivia(pool, visited) {
    const avail  = pool.filter((p) => !visited.some((v) => v.toLowerCase() === p.toLowerCase()));
    const sample = avail.slice(0, 10).join(", ");
    return `THEME — World Landmarks & Geography:
The wrong value should be one of these landmarks, or a wrong measurement/date: ${sample}.

VALID ANOMALY TYPES:
  • Wrong height: "[Subject] stands [X] metres tall" — differ from real by at least 30%
  • Wrong year: "[Subject] was completed / opened / built in [wrong year]"
  • Wrong location: "[Subject] is located in [Wrong City or Country]"
  • Wrong visitor count: "[Subject] receives approximately [X] million visitors annually"
  • Wrong comparison: "[Subject] is taller / larger / older than [wrong landmark]"

MEASUREMENT RULE: Wrong numbers must differ from reality by at least 30%. Never change 324 m to 310 m — a player must be able to say "that seems way off".`;
  },

  random() {
    return `THEME — General Wikipedia:
The wrong value must be a real entity with its own Wikipedia article — a place, person, organization, or event.

DOMAIN RULE: The wrong value must belong to the same domain as the article subject. A scientific article gets a wrong scientific entity. A historical event article gets a wrong historical entity. Never cross domains (e.g., inserting a pop star name into a physics article).

Choose a wrong value that creates a natural and interesting navigation target for the next article.`;
  },
};

// ─────────────────────────────────────────────────────────────
//  DIFFICULTY CALIBRATION
//
//  Scholar  → laugh-out-loud obvious, any casual reader catches it
//  Detective → plausible but wrong, requires domain knowledge
//  Oracle   → near-invisible, requires deliberate fact-checking
// ─────────────────────────────────────────────────────────────

const DIFFICULTY_INSTRUCTIONS = {

  scholar: `DIFFICULTY — SCHOLAR (Easy):
The anomaly must be VERY obvious to a casual reader within ~5–10 seconds.
It should "pop" while still reading like normal Wikipedia prose (no goofy formatting).

TECHNIQUES:
  • Replace a TRUE value from the context with an obviously wrong one in the SAME CATEGORY:
      - true place → very wrong place
      - true date/year → very wrong date/year
      - true person → very wrong person
      - true organization → very wrong organization
  • Make it conspicuously wrong, but still a real Wikipedia entity with its own page.

Subtlety is the enemy of Scholar.`,

  detective: `DIFFICULTY — DETECTIVE (Medium):
The anomaly must be catchable by a SMART READER who reads carefully — even without expert knowledge of the topic.
Two ways the player should be able to spot it:
  (A) They already know the subject well enough to recognise the fact is wrong, OR
  (B) They read the article carefully and can use CONTEXT CLUES within the article itself to catch it.

CRITICAL DESIGN GOAL: If the article mentions "X" elsewhere, and your anomaly says "Y" instead of "X", a careful reader can catch it by cross-referencing within the same page. This is the ideal Detective anomaly.

GOOD TECHNIQUES:
  • Use a name that appears elsewhere in the article — but in the WRONG role. E.g. if the article says "Trajan expanded the empire" earlier, a good anomaly might say "Rome reached its peak under Hadrian" — a careful reader will notice the contradiction.
  • A wrong year that is noticeably off — not 1 year (Oracle) but not 50 years (Scholar) — aim for 10–20 years, enough that it clashes with a date mentioned elsewhere in the article.
  • A real place that is clearly in the wrong region — "Moscow" instead of "London", not "Lyon" instead of "Paris".
  • A wrong but real person who is mentioned elsewhere in the article in a different capacity.

WHAT TO AVOID:
  • Do NOT use subtle 1–3 year shifts (Oracle territory)
  • Do NOT use completely obscure facts that require specialist knowledge to check
  • Do NOT invent a relationship that has no connection to anything else in the article

NUMERIC RULE: If you use numbers, the wrong number must differ from reality by at least ~25–40% and be round/recognisable.`,

  oracle: `DIFFICULTY — ORACLE (Hard):
The anomaly must be NEARLY INDISTINGUISHABLE from truth without deliberate fact-checking. The average reader will read it twice and still believe it.

TECHNIQUES:
  • Change a year by exactly 1–3 years ("1963" instead of "1961")
  • Use a neighboring city or region in the same country ("Lyon" instead of "Paris", "Hamburg" instead of "Berlin")
  • Use a person who held an almost identical role in a very similar context (same organization, slightly different title)
  • Change one small but critical number ("52 states" instead of "50", "silver medal" instead of "gold")
  • Flip one word in a title or role ("Deputy Secretary" vs. "Under-Secretary", "co-founder" vs. "founder")

The anomaly sentence must read perfectly naturally. Only someone who knows the specific fact by heart will catch it.`,
};

// ─────────────────────────────────────────────────────────────
//  MAIN EXPORT — generateAnomalyWithAI
// ─────────────────────────────────────────────────────────────

/**
 * Generate one false fact for a Wikipedia article page.
 *
 * @param {string}   title              Wikipedia article title
 * @param {string}   firstParagraph     Plain text of first 1–3 paragraphs
 * @param {string}   difficulty         "scholar" | "detective" | "oracle"
 * @param {string}   themeId            Theme key for pool + domain selection
 * @param {string[]} themePool          Available pages in this theme
 * @param {string[]} visitedPages       Already-visited titles (avoid loops)
 * @param {string[]} usedFactCategories Categories used this session (enforce variety)
 *
 * @returns anomaly object | null
 *
 * Anomaly object shape:
 *   { sentence, wrongValue, realValue, factLabel, factCategory,
 *     hints: string[], searchTerm, source: "ai" }
 */
export async function generateAnomalyWithAI({
  title,
  firstParagraph,
  difficulty         = "detective",
  themeId            = "random",
  themePool          = [],
  visitedPages       = [],
  usedFactCategories = [],
}) {
  const themeRule = (THEME_INSTRUCTIONS[themeId] || THEME_INSTRUCTIONS.random)(themePool, visitedPages);
  const diffRule  = DIFFICULTY_INSTRUCTIONS[difficulty] || DIFFICULTY_INSTRUCTIONS.detective;

  // Prevent reuse of already-visited pages as wrong values (no navigation loops)
  const avoidNote = visitedPages.length > 0
    ? `\nAVOID — do NOT use any of these as the wrong value (already visited this session): ${visitedPages.slice(-12).join(", ")}`
    : "";

  // Enforce fact-category variety across the game session
  const categoryNote = usedFactCategories.length > 0
    ? `\nCATEGORY VARIETY — These fact categories have already been used this session. You MUST choose a DIFFERENT one: ${usedFactCategories.join(", ")}.\nAvailable categories: person | location | organization | achievement | relationship`
    : "";

  // Hint depth scales with difficulty:
  //   Scholar   → 3 progressive hints (vague → specific → near-reveal)
  //   Detective → 1 moderate hint
  //   Oracle    → none
  const hintCount = difficulty === "scholar" ? 3 : difficulty === "detective" ? 1 : 0;
  const hintInstruction = hintCount === 0
    ? `"hints": []`
    : hintCount === 1
    ? `"hints": ["A single moderate hint — name the category without revealing the answer. E.g. 'The anomaly involves a location.' or 'Think about a specific date.' or 'Consider who this person is associated with professionally.'"]`
    : `"hints": [
    "Hint 1 (vague) — name only the broad category. E.g. 'The anomaly involves a person.' or 'Think about a location.' or 'Consider a specific date.'",
    "Hint 2 (specific) — narrow it down without giving the answer. E.g. 'The wrong fact names someone in the same field as the subject, but the wrong one.' or 'The location mentioned is on the wrong continent.'",
    "Hint 3 (near-reveal) — almost gives it away. E.g. 'The person named held a completely different role in real life — think about who actually held this position.' or 'The city mentioned is in the right country but the wrong region.'"
  ]`;

  const system = `You are a precise, rule-following content generator for WikiScav, a Wikipedia scavenger hunt game.
Players read Wikipedia articles and must find ONE deliberately inserted false fact — the "anomaly."
The anomaly sentence must blend naturally into the existing Wikipedia prose.
You must follow ALL rules exactly. Respond with valid JSON only — no markdown, no explanation.`;

  const user = `Generate ONE anomaly (false fact) about "${title}" to secretly insert into its Wikipedia article.

ARTICLE CONTEXT — your source of truth. Read every sentence:
"""
${firstParagraph.slice(0, 1400)}
"""

━━━ HOW TO BUILD THE ANOMALY — 5 steps ━━━

STEP 1: Find a specific TRUE fact in the article text above.
  Look for a concrete name, date, place, record, or role that is stated in the article.

STEP 2: That fact's key value becomes realValue.
  realValue = the exact value as stated in the article (the true, correct answer).
  realValue MUST come from the article text above — not from your general knowledge.

STEP 3: Replace realValue with a plausible FALSE alternative → wrongValue.
  wrongValue = your false substitution. Must be real, same domain as realValue, has its own Wikipedia page.
  IMPORTANT: Do NOT use dates, years, or numbers as the anomaly. No numeric/date swaps at all.

STEP 4: Write the sentence using wrongValue (not realValue).
  The sentence contains wrongValue. realValue does NOT appear in the sentence.

STEP 5: Self-check — swap wrongValue back to realValue. Does the sentence become TRUE?
  YES → valid anomaly. NO → start over, your sentence was already false or already true.

━━━ EXAMPLE OF CORRECT PROCESS ━━━
Article about Usain Bolt says: "Bolt broke the record previously held by Asafa Powell"
  realValue  = "Asafa Powell"  (found in article — TRUE)
  wrongValue = "Carl Lewis"    (plausible false substitute — same sport, not in article)
  sentence   = "Bolt broke the 100m world record previously held by Carl Lewis."
  ✓ Swap back: "...held by Asafa Powell" = TRUE. Anomaly is valid.

━━━ EXAMPLE OF FAILURE — what NOT to do ━━━
Article about Thomas Edison / the light bulb:
  ✗ sentence = "Edison invented the light bulb" + wrongValue = "Thomas Edison" + realValue = "James Watt"
  Why it fails: Edison DID invent the light bulb — sentence is TRUE, not an anomaly.
  Why it fails: James Watt invented the steam engine — wrong domain entirely for realValue.
  The AI invented both values without reading the article. THIS IS FORBIDDEN.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${themeRule}

${diffRule}
${avoidNote}
${categoryNote}

STYLE REQUIREMENTS (critical):
- Match Wikipedia tone: neutral, formal, third-person, no hype, no jokes, no "fun fact" vibe.
- The sentence must read like it already belongs in the provided article context.
- Prefer a substitution that fits the surrounding topic (same field/role), not a generic "is known as a X" unless the context clearly supports that structure.

RULES:
1. One complete encyclopedic sentence, sounds natural in Wikipedia prose
2. Subject is "${title}" or well-known short form — no pronouns as subject
3. wrongValue is a real entity with its own unambiguous Wikipedia article
4. wrongValue MUST NOT be the article subject ("${title}") and must not be a substring of the title
5. Every word EXCEPT wrongValue is factually accurate about "${title}"
6. wrongValue appears VERBATIM in the sentence
7. realValue does NOT appear in the sentence
8. realValue and wrongValue must be from the same domain (same sport / same field / same era)
9. realValue MUST be copied verbatim from the article text above — not invented from general knowledge
10. No romantic/personal relationships (wife, husband, partner, etc.)
11. Person names must be full proper names — no single first names like "Dorothy"
12. No dates/years/numbers anywhere in wrongValue/realValue/sentence
13. The sentence must be ACTUALLY FALSE with wrongValue in it

SEARCH TERM: "searchTerm" = exact canonical Wikipedia title for wrongValue.
  "replica of Eiffel Tower" → "Eiffel Tower" | "French" → "France"
  Never a phrase, fragment, or adjective.

JSON RESPONSE:
{
  "sentence":     "Encyclopedic sentence with wrongValue in it (the false version)",
  "wrongValue":   "The FALSE substitution — verbatim in sentence",
  "realValue":    "The TRUE value from the article text — same domain, NOT in sentence",
  "factLabel":    "Short label e.g. 'birthplace', 'founding year', 'predecessor'",
  "factCategory": "Exactly one of: person | location | organization | achievement | relationship",
  "searchTerm":   "Exact canonical Wikipedia title for wrongValue",
  ${hintInstruction}
}`;

  // Slightly lower temperature for more consistent encyclopedic tone.
  const parsed = await callAI(system, user, 0.55, 650);
  if (!parsed) return null;

  // ── Basic field validation ───────────────────────────────────
  if (!parsed.sentence || !parsed.wrongValue || !parsed.realValue) {
    console.warn("[WikiScav AI] Incomplete response — missing required fields:", parsed);
    return null;
  }

  // wrongValue must appear verbatim in the sentence
  if (!parsed.sentence.toLowerCase().includes(parsed.wrongValue.toLowerCase())) {
    console.warn("[WikiScav AI] wrongValue not found verbatim in sentence:", parsed);
    return null;
  }

  // searchTerm sanity check — reject fragments longer than 60 chars or containing punctuation
  const rawST = (parsed.searchTerm || parsed.wrongValue || "").trim();
  const searchTerm = rawST.length > 0 && rawST.length <= 60 && !/[,.]/.test(rawST)
    ? rawST
    : parsed.wrongValue.trim();

  const hints = Array.isArray(parsed.hints) ? parsed.hints.filter(Boolean) : [];

  return {
    sentence:     parsed.sentence.trim(),
    wrongValue:   parsed.wrongValue.trim(),
    realValue:    parsed.realValue.trim(),
    factLabel:    (parsed.factLabel    || "fact").trim(),
    factCategory: (parsed.factCategory || "").trim().toLowerCase(),
    hints,
    searchTerm,
    source: "ai",
  };
}
